import { QueryKey, useIsMutating, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/toast/useToast";
import IotZarizeniService, {
    FiltrEvidence, IotKanal, IotPravidlo, IotZarizeni, IotZarizeniRequest, KatalogModel, NoveZarizeni, StavZarizeni,
    VysledekOvladani, OVLADANA, ovladateleKomponenty, vychoziNazevKanalu,
} from "./IotZarizeniService";

export const IOT_ZARIZENI_KEYS = {
    vse: ["iotZarizeni"] as const,
    objektu: (o: number) => ["iotZarizeni", "objekt", o] as const,
    hledej: (f: FiltrEvidence) => ["iotZarizeni", "hledej", f] as const,
    katalog: ["iotKatalog"] as const,
    nova: ["iotZarizeni", "nova"] as const,
    nezarazena: ["iotZarizeni", "nezarazena"] as const,
    stav: (id: number) => ["iotZarizeniStav", id] as const,
    prikazy: (id: number, k: number) => ["iotKanalPrikazy", id, k] as const,
};

export const POPIS_STAVU: Record<string, string> = {
    POTVRZENO: "Zařízení potvrdilo příkaz", PRESKOCENO: "Příkaz přeskočen", NEODPOVEDELO: "Zařízení neodpovědělo",
    CHYBA: "Příkaz se nepovedl", GATEWAY_NEDOSTUPNA: "Server pro zařízení je nedostupný",
    ODMITNUTO: "Server pro zařízení příkaz odmítl",
};

/**
 * Seznamy evidence se obnovují po 30 s, jen když je stránka vidět: schválení nebo zamítnutí v dashboardu správce
 * se tak propíše bez obnovení stránky (E3). Na jeden klíč má časovač jen jeden observer, ostatní čtou cache (`poll = false`).
 */
export const INTERVAL_EVIDENCE_MS = 30000;
const polling = (poll: boolean) => (poll
    ? { refetchInterval: INTERVAL_EVIDENCE_MS, refetchIntervalInBackground: false }
    : { staleTime: INTERVAL_EVIDENCE_MS });
export const useZarizeniObjektuQuery = (objektId: number, poll = true) =>
    useQuery<IotZarizeni[], Error>({
        queryKey: IOT_ZARIZENI_KEYS.objektu(objektId), queryFn: () => IotZarizeniService.objektu(objektId), enabled: !!objektId, ...polling(poll),
    });
export const useHledejZarizeniQuery = (f: FiltrEvidence) =>
    useQuery<IotZarizeni[], Error>({ queryKey: IOT_ZARIZENI_KEYS.hledej(f), queryFn: () => IotZarizeniService.hledej(f), ...polling(true) });
export const useKatalogQuery = () =>
    useQuery<KatalogModel[], Error>({ queryKey: IOT_ZARIZENI_KEYS.katalog, queryFn: IotZarizeniService.katalog, staleTime: 10 * 60 * 1000 });
export const useNovaZarizeniQuery = () =>
    useQuery<NoveZarizeni[], Error>({ queryKey: IOT_ZARIZENI_KEYS.nova, queryFn: IotZarizeniService.nova, ...polling(true) });
export const useNezarazenaQuery = () =>
    useQuery({ queryKey: IOT_ZARIZENI_KEYS.nezarazena, queryFn: IotZarizeniService.nezarazena });
const INTERVAL_STAVU_MS = 15000;
/**
 * Stav jednoho zařízení. `poll = false`: jen čte sdílenou cache, dotazování drží jiný observer (detail objektu),
 * aby na stejný klíč nešly dva nezávislé časovače (review K6-K9, Q2).
 */
export const useStavZarizeniQuery = (id: number, enabled = true, poll = true) =>
    useQuery<StavZarizeni, Error>({
        queryKey: IOT_ZARIZENI_KEYS.stav(id), queryFn: () => IotZarizeniService.stav(id), retry: false, enabled,
        ...(poll ? { refetchInterval: INTERVAL_STAVU_MS } : { staleTime: INTERVAL_STAVU_MS }),
    });
/**
 * Stavy více zařízení najednou (souhrn záložky Zařízení, řádek stavu v hlavičce objektu). Stejné klíče
 * a nastavení jako useStavZarizeniQuery, takže React Query dotazy s kartami zařízení sdílí.
 */
export type StavyZarizeni = Record<number, { data?: StavZarizeni; isError: boolean; isPending?: boolean }>;
export const useStavyZarizeniQuery = (ids: number[]): StavyZarizeni => {
    const vysledky = useQueries({
        queries: ids.map((id) => ({
            queryKey: IOT_ZARIZENI_KEYS.stav(id), queryFn: () => IotZarizeniService.stav(id), refetchInterval: INTERVAL_STAVU_MS, retry: false,
        })),
    });
    const mapa: StavyZarizeni = {};
    ids.forEach((id, i) => { mapa[id] = { data: vysledky[i]?.data, isError: !!vysledky[i]?.isError, isPending: !!vysledky[i]?.isPending }; });
    return mapa;
};
export const usePrikazyKanaluQuery = (id: number, kanalId: number, enabled: boolean) =>
    useQuery({ queryKey: IOT_ZARIZENI_KEYS.prikazy(id, kanalId), queryFn: () => IotZarizeniService.prikazy(id, kanalId), enabled });

type Zprava = { text: string; typ: "success" | "warning" };

const useIotMutation = <R, V>(cfg: {
    mutationFn: (v: V) => Promise<R>; invalidate: (v: V) => QueryKey[]; uspech: (r: R, v: V) => Zprava; chyba: string; mutationKey?: QueryKey;
}) => {
    const { showToast } = useToast();
    const qc = useQueryClient();
    return useMutation<R, Error, V>({
        mutationKey: cfg.mutationKey,
        mutationFn: cfg.mutationFn,
        onSuccess: (r, v) => {
            cfg.invalidate(v).forEach((queryKey) => qc.invalidateQueries({ queryKey }));
            const { text, typ } = cfg.uspech(r, v);
            showToast(text, typ);
        },
        onError: (e) => showToast(e.message || cfg.chyba, "error"),
    });
};

const ok = (text: string) => (): Zprava => ({ text, typ: "success" });

export const useUlozZarizeniMutation = () => useIotMutation<IotZarizeni, { id?: number; data: IotZarizeniRequest }>({
    mutationFn: ({ id, data }) => (id ? IotZarizeniService.uprav(id, data) : IotZarizeniService.vytvor(data)),
    invalidate: () => [IOT_ZARIZENI_KEYS.vse], uspech: ok("Zařízení bylo uloženo"), chyba: "Zařízení se nepodařilo uložit",
});
export const useSmazZarizeniMutation = () => useIotMutation<void, number>({
    mutationFn: (id) => IotZarizeniService.smaz(id),
    invalidate: () => [IOT_ZARIZENI_KEYS.vse], uspech: ok("Zařízení bylo odebráno"), chyba: "Zařízení se nepodařilo odebrat",
});
/** kanalId chybí = přidat kanál */
export const useUlozKanalMutation = () => useIotMutation<IotKanal, { id: number; kanalId?: number; data: { komponenta: string; nazev: string; aktivni?: boolean } }>({
    mutationFn: ({ id, kanalId, data }) => (kanalId
        ? IotZarizeniService.upravKanal(id, kanalId, { ...data, aktivni: data.aktivni ?? true })
        : IotZarizeniService.pridejKanal(id, data)),
    invalidate: () => [IOT_ZARIZENI_KEYS.vse], uspech: ok("Výstup byl uložen"), chyba: "Výstup se nepodařilo uložit",
});
export const useSmazKanalMutation = () => useIotMutation<void, { id: number; kanalId: number }>({
    mutationFn: ({ id, kanalId }) => IotZarizeniService.smazKanal(id, kanalId),
    invalidate: () => [IOT_ZARIZENI_KEYS.vse], uspech: ok("Výstup byl odebrán"), chyba: "Výstup se nepodařilo odebrat",
});
export const OBNOVA_VYPNUTA_PRAVIDLEM = "Obnova stavu po výpadku proudu byla u kanálu vypnuta, protože pravidlo používá časovač.";
/** Zpráva po uložení nebo odebrání pravidla; když BE kvůli časovači vypnul obnovu po výpadku proudu, oznámí to. */
export const zpravaPoUlozeniPravidla = (r: IotKanal | void | undefined, data: IotPravidlo | null): Zprava => {
    if (!data) return { text: "Pravidlo bylo odebráno", typ: "success" };
    if (r && r.typ === "switch" && r.obnovaVypnutaPravidlem) return { text: `Pravidlo bylo uloženo. ${OBNOVA_VYPNUTA_PRAVIDLEM}`, typ: "warning" };
    return { text: "Pravidlo bylo uloženo", typ: "success" };
};
export const useUlozPravidloMutation = () => useIotMutation<IotKanal | void, { id: number; kanalId: number; data: IotPravidlo | null }>({
    mutationFn: ({ id, kanalId, data }) => (data ? IotZarizeniService.ulozPravidlo(id, kanalId, data) : IotZarizeniService.smazPravidlo(id, kanalId)),
    invalidate: () => [IOT_ZARIZENI_KEYS.vse],
    uspech: (r, { data }) => zpravaPoUlozeniPravidla(r, data),
    chyba: "Pravidlo se nepodařilo uložit",
});
export const useNastavObnovuMutation = () => useIotMutation<IotKanal, { id: number; kanalId: number; obnovitPoVypadku: boolean }>({
    mutationFn: ({ id, kanalId, obnovitPoVypadku }) => IotZarizeniService.nastavObnovu(id, kanalId, obnovitPoVypadku),
    invalidate: () => [IOT_ZARIZENI_KEYS.vse],
    uspech: (_, { obnovitPoVypadku }) => ({
        text: obnovitPoVypadku ? "Po výpadku proudu se stav výstupu obnoví" : "Po výpadku proudu zůstane výstup vypnutý", typ: "success",
    }),
    chyba: "Nastavení obnovy po výpadku proudu se nepodařilo uložit",
});
/** Server pro zařízení odmítl příkaz, protože zařízení nemá schválení správce (režim blokování příkazů). */
export const ODMITNUTO_NESCHVALENO = "Server pro zařízení příkaz odmítl, protože zařízení zatím nemá schválení od správce systému Hasiči Online.";
/**
 * Text výsledku ručního příkazu. Odmítnutí kvůli schválení se hlásí srozumitelně a bez pokynu gatewaye
 * „schvalte ho v evidenci“ (jednotka schválení nemění, povoluje jen správce systému).
 */
export const textVysledkuOvladani = (v: VysledekOvladani): string => {
    if (v.stav === "ODMITNUTO" && /schvál/i.test(v.poznamka ?? v.message ?? "")) return ODMITNUTO_NESCHVALENO;
    return v.stav !== "POTVRZENO" && v.poznamka ? `${POPIS_STAVU[v.stav] ?? v.stav}: ${v.poznamka}` : POPIS_STAVU[v.stav] ?? v.stav;
};
/** Volá každá KanalKarta zvlášť: čekání jednoho kanálu neblokuje ostatní. */
type OvladaniKanalu = { id: number; kanalId: number; akce: string; sekund?: number; hodnota?: number };
const KLIC_OVLADANI = ["iotOvladani"] as const;
export const useOvladejKanalMutation = () => useIotMutation<VysledekOvladani, OvladaniKanalu>({
    mutationKey: KLIC_OVLADANI,
    mutationFn: ({ id, kanalId, akce, sekund, hodnota }) => IotZarizeniService.ovladej(id, kanalId, akce, sekund, hodnota),
    invalidate: ({ id, kanalId }) => [IOT_ZARIZENI_KEYS.stav(id), IOT_ZARIZENI_KEYS.prikazy(id, kanalId)],
    uspech: (v) => ({ text: textVysledkuOvladani(v), typ: v.success ? "success" : "warning" }),
    chyba: "Příkaz se nepodařilo odeslat",
});
/**
 * Běží ruční příkaz výstupu (kromě Zastavit)? Čte z cache mutací, ne ze stavu komponenty, takže
 * „Odesílám…“ přežije sbalení karty i přepnutí záložky a druhý příkaz nejde poslat, dokud první neskončí.
 */
export const useOvladaniProbiha = (id: number, kanalId: number): boolean => useIsMutating({
    mutationKey: KLIC_OVLADANI,
    predicate: (m) => {
        const v = m.state.variables as OvladaniKanalu | undefined;
        return !!v && v.id === id && v.kanalId === kanalId && v.akce !== "COVER_STOP";
    },
}) > 0;
export const useRozpoznatMutation = () => useIotMutation<void, number>({
    mutationFn: (id) => IotZarizeniService.rozpoznat(id),
    invalidate: (id) => [IOT_ZARIZENI_KEYS.stav(id)], uspech: ok("Rozpoznání spuštěno, výsledek se objeví do minuty"),
    chyba: "Rozpoznání se nepodařilo spustit",
});
/** Převod zařízení na požadavek úprav (beze změny kanálů; ty BE při úpravě nečte). */
export const naPozadavek = (z: IotZarizeni): IotZarizeniRequest => ({
    objektId: z.objektId, protokol: z.protokol, identifikator: z.identifikator, gatewayId: z.gatewayId, modelKod: z.modelKod,
    kategorie: z.kategorie, vyrobce: z.vyrobce, modelNazev: z.modelNazev, nazev: z.nazev, umisteni: z.umisteni,
    vyrobniCislo: z.vyrobniCislo, datumInstalace: z.datumInstalace, poznamka: z.poznamka, aktivni: z.aktivni,
    puvodniIdentifikator: z.identifikator, puvodniGatewayId: z.gatewayId,
});

/** Ovládané komponenty, které zná model z katalogu nebo rozpoznání (bez duplicit, v pořadí model, rozpoznání). */
export const znameKomponenty = (model: KatalogModel | undefined, stav: StavZarizeni | undefined): string[] =>
    ovladateleKomponenty(model, stav?.profil ?? '')
        .concat((stav?.komponenty ?? []).filter((k) => OVLADANA.test(k)))
        .filter((k, i, a) => a.indexOf(k) === i);

/**
 * Co by „Převzít z rozpoznání“ změnilo: model (jen když je rozpoznaný model v katalogu a liší se)
 * a ovládané komponenty z modelu a rozpoznání, které zařízení ještě nemá jako kanál.
 */
export const kPrevzeti = (zarizeni: IotZarizeni, stav: StavZarizeni | undefined, katalog: KatalogModel[]): { model?: KatalogModel; kanaly: string[] } => {
    const rozpoznany = stav?.modelKod ? katalog.find((m) => m.kod === stav.modelKod) : undefined;
    const ma = new Set(zarizeni.kanaly.map((k) => k.komponenta));
    return {
        model: rozpoznany && rozpoznany.kod !== zarizeni.modelKod ? rozpoznany : undefined,
        kanaly: znameKomponenty(rozpoznany, stav).filter((k) => !ma.has(k)),
    };
};

/** Chyba v průběhu převzetí: nese, kolik kanálů už bylo přidáno a zda se uložil model. */
export class ChybaPrevzeti extends Error {
    constructor(message: string, public readonly pridano: number, public readonly modelUlozen: boolean) { super(message); }
}

/**
 * „Převzít z rozpoznání“ (spec katalogu 8, review M5): uloží rozpoznaný model (když je v katalogu) a přidá
 * ovládané kanály z rozpoznaných komponent, které zařízení ještě nemá. Existující kanály se nemění.
 * Seznam se obnoví i po chybě, protože část kanálů už mohla vzniknout.
 */
export const usePrevzitRozpoznaniMutation = () => {
    const { showToast } = useToast();
    const qc = useQueryClient();
    return useMutation<number, Error, { zarizeni: IotZarizeni; stav: StavZarizeni; katalog: KatalogModel[] }>({
        mutationFn: async ({ zarizeni, stav, katalog }) => {
            const { model, kanaly } = kPrevzeti(zarizeni, stav, katalog);
            let pridano = 0;
            let modelUlozen = false;
            try {
                if (model) {
                    await IotZarizeniService.uprav(zarizeni.id, { ...naPozadavek(zarizeni), modelKod: model.kod, kategorie: model.kategorie });
                    modelUlozen = true;
                }
                for (const komponenta of kanaly) {
                    await IotZarizeniService.pridejKanal(zarizeni.id, { komponenta, nazev: vychoziNazevKanalu(komponenta) });
                    pridano++;
                }
            } catch (e) {
                throw new ChybaPrevzeti(e instanceof Error ? e.message : String(e), pridano, modelUlozen);
            }
            return pridano;
        },
        onSettled: () => qc.invalidateQueries({ queryKey: IOT_ZARIZENI_KEYS.vse }),
        onSuccess: (n) => showToast(n ? `Převzato, přidané výstupy: ${n}` : "Převzato, výstupy už existují", "success"),
        onError: (e) => {
            const pridano = e instanceof ChybaPrevzeti ? e.pridano : 0;
            const model = e instanceof ChybaPrevzeti && e.modelUlozen ? ", model uložen" : "";
            showToast(`Rozpoznání se nepodařilo převzít celé (přidané výstupy: ${pridano}${model})${e.message ? `: ${e.message}` : ""}`, "error");
        },
    });
};
